import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useAuth from "../../../../auth/useAuth";
import { getSalesOrdersRoute } from "../../../../config";

const currency = (v) => `$${Number(v ?? 0).toFixed(2)}`;

const SalesRowDetailDialog = ({ open, onClose, row }) => {
  const auth = useAuth();
  const token = auth.globals.token;

  const [page, setPage] = useState(1);
  const [ordersData, setOrdersData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchOrders = async (targetPage) => {
    if (!row) return;
    setLoading(true);
    try {
      const res = await fetch(
        getSalesOrdersRoute({
          init_date: row.period,
          end_date: row.period,
          companies: [row.company_id],
          countries: [row.country_id],
          page: targetPage,
        }),
        { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } }
      );
      if (res.ok) {
        setOrdersData(await res.json());
      } else {
        setOrdersData(null);
      }
    } catch {
      setOrdersData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && row) {
      setPage(1);
      setOrdersData(null);
      fetchOrders(1);
    }
  }, [open, row]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchOrders(newPage);
  };

  const orders = ordersData?.orders ?? [];
  const totalPages = ordersData?.total_pages ?? 1;
  const total = ordersData?.total ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {row ? `${row.company_name} — ${row.country_name} — ${row.period}` : ""}
        {total > 0 && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({total.toLocaleString()} órdenes)
          </Typography>
        )}
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography align="center" color="text.secondary" p={4}>
            No hay órdenes para este período.
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Correlativo</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Ciudad</strong></TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                    <TableCell align="right"><strong>Costo</strong></TableCell>
                    <TableCell align="right"><strong>Cobrado</strong></TableCell>
                    <TableCell align="right"><strong>Pendiente</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Button
                          size="small"
                          color="secondary"
                          sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: "bold" }}
                          onClick={() => window.open(`/PickupForm?id=${order.correlative}`, "_blank")}
                        >
                          {order.correlative}
                        </Button>
                      </TableCell>
                      <TableCell>{order.senders_name}</TableCell>
                      <TableCell>{order.senders_city}</TableCell>
                      <TableCell>{order.senders_state}</TableCell>
                      <TableCell align="right">{currency(order.delivery_cost)}</TableCell>
                      <TableCell align="right">{currency(order.collected)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: Number(order.pendiente) > 0 ? "error.main" : "inherit", fontWeight: Number(order.pendiente) > 0 ? "bold" : "normal" }}
                      >
                        {currency(order.pendiente)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" alignItems="center" gap={2} py={1.5}>
              <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                Anterior
              </Button>
              <Typography variant="body2">
                Página {page} de {totalPages}
              </Typography>
              <Button size="small" variant="outlined" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
                Siguiente
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SalesRowDetailDialog;
